import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage immediately to prevent empty cart on refresh
  const getInitialCart = () => {
    try {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
        const parsed = JSON.parse(savedCart);
        // Ensure we have a valid array with valid items
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(item => item && item.id && typeof item.quantity === 'number' && item.quantity > 0);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return [];
  };

  const [cart, setCart] = useState(getInitialCart);
  const [isInitialized, setIsInitialized] = useState(false);

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Save cart to localStorage whenever it changes (after initialization to prevent overwriting on refresh)
  useEffect(() => {
    if (isInitialized) {
    localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart, isInitialized]);

  const getEffectivePrice = useCallback((product, selectedOptions = null) => {
    let originalPrice = parseFloat(product.variant?.price || product.price) || 0;
    
    // If product has options and selected options, try to get price from option values
    if (product.options && selectedOptions && Object.keys(selectedOptions).length > 0) {
      // Find price from selected option values (prioritize first selected option's value)
      for (const option of product.options) {
        if (option.values && selectedOptions[option.id]) {
          const selectedValue = option.values.find(v => v.id === selectedOptions[option.id]);
          if (selectedValue && selectedValue.price !== null && selectedValue.price !== undefined) {
            originalPrice = parseFloat(selectedValue.price);
            break; // Use first found price
          }
        }
      }
    }
    
    const discountPercentage = parseFloat(product.discount_percentage) || 0;
    
    if (discountPercentage > 0) {
      return originalPrice * (1 - discountPercentage / 100);
    }
    return originalPrice;
  }, []);

  // Maximum orderable quantity for a cart line (variant stock, else product
  // stock). null = unknown/unlimited. The backend re-validates on submit.
  const getMaxQuantity = useCallback((product) => {
    const stock = Number(product?.variant?.stock_quantity ?? product?.stock_quantity);
    return Number.isFinite(stock) && stock > 0 ? Math.floor(stock) : null;
  }, []);

  const clampQuantity = useCallback((product, quantity) => {
    const max = getMaxQuantity(product);
    return max !== null ? Math.min(quantity, max) : quantity;
  }, [getMaxQuantity]);

  const getCartItemKey = useCallback((product, selectedOptions = null) => {
    if (!selectedOptions || Object.keys(selectedOptions).length === 0) {
      return `product_${product.id}`;
    }
    // Sort option IDs to ensure consistent key generation
    const optionKeys = Object.keys(selectedOptions).sort().map(optId => `${optId}_${selectedOptions[optId]}`).join('_');
    return `product_${product.id}_${optionKeys}`;
  }, []);

  const addToCart = useCallback((product, quantity = 1, selectedOptions = null) => {
    setCart(prevCart => {
      const cartItemKey = getCartItemKey(product, selectedOptions);
      
      // Find existing item with same product and options
      const existingItem = prevCart.find(item => {
        const itemKey = getCartItemKey(item, item.selectedOptions || null);
        return itemKey === cartItemKey;
      });
      
      // Calculate effective price (with discount applied and option prices considered)
      const effectivePrice = getEffectivePrice(product, selectedOptions);
      
      // Build cart item with variant information
      const cartItem = {
        ...product,
        quantity: clampQuantity(product, quantity),
        effectivePrice,
        selectedOptions: selectedOptions || null,
        cartItemKey // Store key for easy identification
      };

      if (existingItem) {
        return prevCart.map(item => {
          const itemKey = getCartItemKey(item, item.selectedOptions || null);
          if (itemKey === cartItemKey) {
            // Recalculate price in case option prices changed
            const updatedPrice = getEffectivePrice(product, selectedOptions);
            return { ...item, quantity: clampQuantity(product, item.quantity + quantity), effectivePrice: updatedPrice };
          }
          return item;
        });
      }
      return [...prevCart, cartItem];
    });
  }, [getCartItemKey, getEffectivePrice, clampQuantity]);

  const removeFromCart = useCallback((productId, cartItemKey = null) => {
    if (cartItemKey) {
      // Remove specific variant
      setCart(prevCart => prevCart.filter(item => {
        const itemKey = getCartItemKey(item, item.selectedOptions || null);
        return itemKey !== cartItemKey;
      }));
    } else {
      // Remove all variants of this product (backward compatibility)
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
    }
  }, [getCartItemKey]);

  const updateQuantity = useCallback((productId, quantity, cartItemKey = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, cartItemKey);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item => {
        if (cartItemKey) {
          // Update specific variant
          const itemKey = getCartItemKey(item, item.selectedOptions || null);
          if (itemKey === cartItemKey) {
            return { ...item, quantity: clampQuantity(item, quantity) };
          }
        } else {
          // Update first matching product (backward compatibility)
          if (item.id === productId) {
            return { ...item, quantity: clampQuantity(item, quantity) };
          }
        }
        return item;
      })
    );
  }, [removeFromCart, getCartItemKey, clampQuantity]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => {
      // Use effectivePrice if available (with discount), otherwise calculate it
      const price = item.effectivePrice !== undefined 
        ? item.effectivePrice 
        : getEffectivePrice(item, item.selectedOptions || null);
      return total + (price * item.quantity);
    }, 0);
  }, [cart, getEffectivePrice]);

  const getCartItemCount = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    getEffectivePrice,
    getCartItemKey,
    getMaxQuantity
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartItemCount, getEffectivePrice, getCartItemKey, getMaxQuantity]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
